<template>
    <!-- Create Panel Modal -->
    <base-modal id="create-panel-modal" size="large" :loading="modalLoading" :centered="true">
        <template>
            <div class="d-flex align-items-center mt-2 mx-3">
                <div class="flex-grow-1">
                    <p class="mb-0 text-secondary">Choose a content type from the list below:</p>
                </div>
                <div class="flex-grow-0">
                    <button class="btn btn-primary" :disabled="createIsDisabled" @click="createPanel()">
                        + Add to Screen
                    </button>
                </div>
            </div>
            <div class="row mx-0 mb-2">
                <div class="col-12 col-sm-6 col-md-4 mt-4" v-for="panelType in panelTypes" :key="panelType.value">
                    <div
                        class="card h-100 panel-type-card rounded-lg"
                        :class="{
                            'panel-type-card-active': panel.panel_type == panelType.value,
                            'panel-type-card-disabled': panelType.disabled,
                            'panel-type-card-available': !panelType.disabled,
                        }"
                        @click="setPanelType(panelType)"
                    >
                        <div class="card-body panel-type-card-body d-flex align-items-center">
                            <div class="flex-fill">
                                <div class="d-flex justify-content-center">
                                    <p
                                        class="panel-icon"
                                        :class="{ 'panel-icon-disabled': panelType.disabled }"
                                        style="margin-bottom: 14px"
                                    >
                                        <span
                                            class="icon create-panel-type-icon"
                                            :class="panelTypeIcon(panelType.value)"
                                        ></span>
                                    </p>
                                </div>
                                <div>
                                    <p class="text-center" style="margin-bottom: 5px">
                                        <span
                                            :class="{
                                                'text-body': !panelType.disabled,
                                                'text-secondary': panelType.disabled,
                                            }"
                                            class="medium panel-label align-middle"
                                            >{{ panelTypeLabel(panelType.value) }}</span
                                        >
                                        <span
                                            v-if="panelType.paidOnly && freeTierActive"
                                            class="badge badge-light badge-tiny ml-1 align-middle"
                                            >Paid Only
                                        </span>
                                        <span
                                            v-else-if="panelType.disabled"
                                            class="ml-1 icon-glyphicons-basic-lock text-secondary"
                                            data-toggle="popover"
                                            data-trigger="hover focus"
                                            data-content="Plan Upgrade Required"
                                            data-placement="top"
                                            data-custom-class="text-center"
                                        ></span>
                                        <span
                                            v-show="panelType.new && !panelType.disabled"
                                            class="badge badge-highlight badge-tiny ml-1 align-middle"
                                        >
                                            New
                                        </span>
                                        <span
                                            v-show="panelType.ai"
                                        >
                                            <ai-badge class="ml-1"></ai-badge>
                                        </span>
                                        <span
                                            v-show="panelType.beta && !panelType.disabled"
                                            class="badge badge-gray badge-tiny ml-1 align-middle"
                                        >
                                            Beta
                                        </span>
                                    </p>
                                    <p class="text-secondary small text-center mb-1">
                                        {{ panelType.description }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template slot="footer">
            <button class="btn btn-secondary" type="button" data-dismiss="modal">Close</button>
            <button @click="createPanel()" :disabled="createIsDisabled" class="btn btn-primary" type="button">
                + Add to Screen
            </button>
        </template>
    </base-modal>
</template>

<script>
import http from "@/axios.js"
import BaseModal from "@/components/BaseModal"
import AiBadge from "@/components/AiBadge"
import { panelTypeIcons, panelTypeLabels, panelTypes } from "@/assets/js/settings.js"

export default {
    name: "PanelCreator",
    props: {
        savedNode: {
            type: Object,
            required: false,
            default() {
                return {
                    panels: [],
                }
            },
        },
    },
    components: {
        BaseModal,
        AiBadge,
    },
    data() {
        return {
            panel: {},
            modalLoading: false,
            panelTypes: [],
            panelWasCreated: false,
        }
    },
    computed: {
        node() {
            return this.savedNode || { panels: [] }
        },
        freeTierActive() {
            return this.$store.getters.freeTierActive
        },
        chatInteractionEnabled() {
            return this.$store.getters.chatInteractionEnabled
        },
        readOnly() {
            return this.$store.getters.caseIsReadOnly
        },
        createIsDisabled() {
            return this.modalLoading || this.readOnly || !this.panel.panel_type
        },
    },
    methods: {
        // Open the create panel modal and initialize the new panel.
        open() {
            this.panelWasCreated = false
            this.initPanelTypes()
            // Initialize panel object for editing.
            this.panel = {
                panel_type: null,
            }
            $("#create-panel-modal").modal("show")
        },
        // Initialize the list of panel types, and set a flag to control the enabled/disabled
        // appearance of each one.
        initPanelTypes() {
            this.panelTypes = []
            let vm = this
            panelTypes.forEach((panelType) => {
                let isDisabled = panelType.paidOnly && vm.freeTierActive
                if (panelType.value === "chat" && !vm.chatInteractionEnabled) {
                    isDisabled = true
                }
                vm.panelTypes.push({
                    ...panelType,
                    disabled: isDisabled,
                })
            })
            vm.$nextTick(() => {
                $('[data-toggle="popover"]').popover()
            })
        },
        createPanel() {
            this.modalLoading = true
            let vm = this
            http.post("/api/nodes/" + this.savedNode.id + "/panels/", this.panel)
                .then(function (response) {
                    vm.panelWasCreated = true
                    $("#create-panel-modal").modal("hide")
                    vm.$emit("created", response.data)
                })
                .finally(function () {
                    vm.modalLoading = false
                })
        },
        setPanelType(panelType) {
            if (panelType.disabled) {
                return
            }
            this.panel.panel_type = panelType.value
        },
        panelTypeIcon(panelType) {
            return panelTypeIcons[panelType]
        },
        panelTypeLabel(panelType) {
            let label = ""
            try {
                label = panelTypeLabels[panelType]
            } catch (error) {
                label = "Content"
            }
            return label
        },
    },
    mounted() {
        let vm = this
        $("#create-panel-modal").on("hidden.bs.modal", function () {
            if (!vm.panelWasCreated) {
                vm.$emit("cancelled")
            }
        })
    },
    beforeDestroy() {
        // Remove event listeners on the modal.
        $("#create-panel-modal").off()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/paneledit.scss";
@import "@/assets/styles/variables.scss";

.panel-icon {
    color: $secondary;
}

.panel-icon-disabled {
    color: #9ca5ad !important;
}

.panel-label {
    font-size: 14px;
    font-weight: 600;
}
</style>